// ─────────────────────────────────────────────────────────────────────────────
// src/lib/gtm.js
// Complete GTM + GA4 utility for ShippingChimp React App
// ─────────────────────────────────────────────────────────────────────────────
import { LABELGENAPI } from './API';

export const GTM_ID = 'GTM-TVKVNP5'
export const GA4_ID = 'G-MY0VDTVCV3'

// ── Safe gtag — never throws "gtag is not defined" ───────────────────────────

export function gtag(...args) {
    if (typeof window === 'undefined') return

    window.dataLayer = window.dataLayer || []

    if (typeof window.gtag === 'function') {
        // gtag loaded and ready
        window.gtag(...args)
    } else {
        // gtag not ready yet — push directly to dataLayer
        // GTM will pick this up when it loads
        window.dataLayer.push(args)
    }
}

// ── Push event to dataLayer ──────────────────────────────────────────────────

export function pushToDataLayer(data) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
}

// ── Read GA4 client_id from _ga cookie ───────────────────────────────────────

export function getGA4ClientId() {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/)
    return match ? match[1] : null
}

// ── Read stored UTMs from sessionStorage / localStorage ──────────────────────

export function getStoredUTMs() {
    return {
        utm_source  : sessionStorage.getItem('utm_source')
                   || localStorage.getItem('utm_source')   || '(direct)',
        utm_medium  : sessionStorage.getItem('utm_medium')
                   || localStorage.getItem('utm_medium')   || '(none)',
        utm_campaign: sessionStorage.getItem('utm_campaign')
                   || localStorage.getItem('utm_campaign') || '(not set)',
        utm_term    : sessionStorage.getItem('utm_term')
                   || localStorage.getItem('utm_term')     || '',
        utm_content : sessionStorage.getItem('utm_content')
                   || localStorage.getItem('utm_content')  || '',
    }
}

// ── Persist UTMs from URL to storage (call on every page load) ───────────────

export function persistUTMs() {
    const params = new URLSearchParams(window.location.search)
    const keys   = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

    keys.forEach(key => {
        if (params.get(key)) {
            sessionStorage.setItem(key, params.get(key))
            localStorage.setItem(key, params.get(key))
            localStorage.setItem('utm_captured_at',  new Date().toISOString())
            localStorage.setItem('utm_captured_url', window.location.href)
        }
    })
}

// ── Set user_id in GA4 ───────────────────────────────────────────────────────

export function setGA4UserId(userId) {
    const id = String(userId)
    gtag('config', GA4_ID, { user_id: id })
    gtag('set',    'user_properties', { dl_user_id: id })

    // Also push to dataLayer so GTM picks it up
    pushToDataLayer({
        user_id   : id,
        dl_user_id: id,
    })
}

// ── Track registration ───────────────────────────────────────────────────────

export async function trackRegistration(profile) {
    const utms     = getStoredUTMs()
    const clientId = getGA4ClientId()
    const userId   = String(profile.id)

    // 1. Set user_id in GA4
    setGA4UserId(userId)

    // 2. Push event to GTM dataLayer
    pushToDataLayer({
        event        : 'sc_user_registered',
        user_id      : userId,
        dl_user_id   : userId,
        ga4_client_id: clientId,
        ...utms
    })

    // 3. Save to ShippingChimp backend
    try {
        return LABELGENAPI.post('/update-analytics', {
            user_id: profile.id,
            client_id: clientId,
            ...utms // This spreads utm_source, utm_medium, etc. into the top level
        });
    } catch (err) {
        console.error('[GTM] Analytics save failed:', err)
    }
}

// ── Track login (links existing users to GA4) ────────────────────────────────

export async function trackLogin(profile) {
    const utms     = getStoredUTMs()
    const clientId = getGA4ClientId()
    const userId   = String(profile.id)

    // 1. Set user_id in GA4
    setGA4UserId(userId)

    // 2. Push event to GTM dataLayer
    pushToDataLayer({
        event        : 'sc_user_logged_in',
        user_id      : userId,
        dl_user_id   : userId,
        ga4_client_id: clientId,
        ...utms
    })

    // 3. Save to backend
    try {
        return LABELGENAPI.post('/update-analytics', {
            user_id: profile.id,
            client_id: clientId,
            ...utms // This spreads utm_source, utm_medium, etc. into the top level
        });
    } catch (err) {
        console.error('[GTM] Analytics save failed:', err)
    }
}